import { getZone, fetchCarrierRoutes } from "components/audience-map/utils";
import type { RecommendedZone } from "./TargetingContext";
import type { TargetingCity } from "./cities";

// A selectable D2D distribution polygon (a mission boundary / carrier route).
export interface D2dBoundary {
  id: string;
  /** Polygon rings in [{lat,lng}…] form, as the legacy data-layer renderer expects. */
  paths: unknown;
  name: string;
  /**
   * Polygon centroid — used to recentre the map when focusing the area. Optional
   * because a boundary may arrive without one; the pan-to-area focus simply
   * no-ops then (it guards on numeric coords) rather than jumping to (0,0).
   */
  center?: { lat: number; lng: number };
  /** Auto-derived coverage units (delivery points), never user-entered (T8). */
  units: number;
}

// A `loadData` from useOverlaysFetch, narrowed to what the boundary fetch needs.
type LoadData = (args: {
  layer: string;
  area?: unknown;
  exclusionZones?: unknown;
  includeAddressCount?: boolean;
  after?: string | null;
}) => Promise<{ data?: Record<string, any>; pageInfo?: { hasNextPage?: boolean; endCursor?: string } } | undefined>;

// Per-area units source (Open Technical Constraint T8). US carrier routes and
// EU mission boundaries both expose residential + apartment delivery counts on
// the feature properties; their sum is the coverage units shown per area and
// summed into the Summary total. Isolated here so the source can be swapped if
// the OADS-settings derivation turns out to differ — confirm with T8 before QA.
const deriveUnits = (metadata: any): number => {
  if (!metadata) return 0;
  const home = Number(metadata.HOME_COUNT) || 0;
  const apt = Number(metadata.APT_COUNT) || 0;
  if (home || apt) return home + apt;
  return Number(metadata.addressCount ?? metadata.deliveryPointCount ?? metadata.count) || 0;
};

const toBoundary = (overlay: any): D2dBoundary => ({
  id: String(overlay.id),
  paths: overlay.paths,
  name: overlay.name ?? "",
  center: overlay.center,
  units: deriveUnits(overlay.metadata),
});

// Fetches the city's selectable D2D polygons. US uses paginated carrier routes
// (no zone filter — the whole city), every other country uses MissionBoundaries
// clipped to the city's distribution zone(s) minus its exclusion zone(s). Both
// paths return the same boundary shape with auto-derived units.
export const fetchD2dBoundaries = async ({
  city,
  zones,
  countryCode,
  loadData,
}: {
  city: TargetingCity;
  zones: RecommendedZone[];
  countryCode: string;
  loadData: LoadData;
}): Promise<D2dBoundary[]> => {
  if (countryCode === "US") {
    const routes = await fetchCarrierRoutes({
      cityId: String(city.id),
      cityName: city.name,
      updateMap: () => undefined,
    });
    return Object.values(routes ?? {}).map(toBoundary);
  }

  // getZone unions the distribution / exclusion zone geometries (turf) the same
  // way the legacy D2D map builds its fetch area.
  const distribution = getZone({ distributionZones: zones }, "distribution");
  const exclusion = getZone({ distributionZones: zones }, "exclusion");
  if (!distribution?.geometry) return [];

  const collected: Record<string, any> = {};
  let after: string | null | undefined;
  // Bounded loop so a misbehaving cursor can't spin forever.
  for (let page = 0; page < 50; page += 1) {
    const res = await loadData({
      layer: "MissionBoundaries",
      area: distribution.geometry,
      exclusionZones: exclusion?.geometry ?? null,
      includeAddressCount: true,
      after,
    });
    Object.assign(collected, res?.data ?? {});
    if (!res?.pageInfo?.hasNextPage) break;
    after = res.pageInfo.endCursor;
  }
  return Object.values(collected).map(toBoundary);
};
